import { Box } from "ui/box";
import { useCallback, useRef } from "react";

import BeeEditor from "containers/admin/clients/touchpoint/components/bee-editor/bee-editor";

import { useBuilderContent } from "hooks/api/use-touchpoint-versions";
import type { LandingPageAttributes } from "hooks/api/use-touchpoint-versions";

import { addAnnotationIdToElements, sanitizeBuilderJson } from "utilities";

import { TouchpointType } from "types/touchpoint";
import { BLANK_BEE_TEMPLATE } from "utilities/constants";

interface LandingPageBuilderDesignTabProps {
  onSave: (field: Record<string, any>) => Promise<void>;
  setIsSaved: (isSaved: boolean) => void;
  setIsSaving: (isSaving: boolean) => void;
}

const LPBuilderDesignTab = ({
  onSave,
  setIsSaved,
  setIsSaving,
}: LandingPageBuilderDesignTabProps) => {
  const initialAttributes = useBuilderContent() as LandingPageAttributes;

  // we need to use a ref so that bee looks at the latest onSave function, otherwise it will use the old one and touchpointContent will not be updated
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  const handleDesignChange = useCallback(
    async (jsonFile: string) => {
      setIsSaving(true);
      try {
        const annotatedJson = addAnnotationIdToElements(jsonFile);
        const sanitizedJson = sanitizeBuilderJson(annotatedJson);

        const designPayload = {
          data: {
            htmlOutput: "<>",
            jsonOutput: sanitizedJson,
          },
          sourceType: "BEE",
        };

        await onSaveRef.current(designPayload);
        setIsSaved(true);
      } catch (error) {
        console.error("Error saving design", error);
        setIsSaved(false);
      }
      setIsSaving(false);
    },
    [setIsSaved, setIsSaving],
  );

  return (
    <Box sx={{ display: "flex", flex: "1", mt: "-1.5rem" }}>
      <BeeEditor
        editorType={TouchpointType.LANDING_PAGE}
        initialJson={initialAttributes.data?.jsonOutput ?? BLANK_BEE_TEMPLATE}
        onChange={handleDesignChange}
      />
    </Box>
  );
};

export default LPBuilderDesignTab;
